import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from './apiConfig';
import type {
  IReleaseNote,
  IReadReleaseNote,
  IProjectNotification,
  IReportNotification,
} from 'models';

// mfg id is hardcoded to 1 in the Backbone header (matches the source).
const MFG_ID = 1;

/** Page returned by the project/report notification endpoints (plain array). */
export interface INotificationPage<T> {
  rows: T[];
  page: number;
}

/**
 * RTK Query api for the global header live dropdowns (§3.A / §4 "Header
 * dropdowns (live on dashboard)"). Reuses the shared `baseQuery`
 * (CSRF + X-Requested-With + 401→/login).
 *
 * Endpoints, URLs, params, and CSRF match the Backbone header (header.js +
 * collection/notes.js + collection/notifications.js) exactly:
 *  - Release notes:   GET /api/v1.0/user_settings/release-note-all/   (AllNotes)
 *                     GET/POST /api/v1.0/user_settings/release-note/  (ReadedNotes)
 *  - Notifications:   GET   /project/project_notifications/1/?page=N  (50/page)
 *                     PATCH /project/project_notifications/1/{id}/    ({is_read:true})
 *  - Reports:         GET   /reports/reports_notifications/1/?page=N  (50/page)
 *                     GET   /reports/count_reports_notification/1/    (X-Notification-Count)
 *                     PATCH /reports/report_notification/1/{id}/      ({is_read:true})
 */
export const headerApi = createApi({
  reducerPath: 'headerApi',
  baseQuery,
  tagTypes: ['ReleaseNotes', 'ReadReleaseNotes', 'Notifications', 'Reports', 'ReportsCount'],
  endpoints: (builder) => ({
    // ---- Release notes ----
    getReleaseNotesAll: builder.query<IReleaseNote[], void>({
      query: () => '/api/v1.0/user_settings/release-note-all/',
      providesTags: ['ReleaseNotes'],
    }),
    getReleaseNotes: builder.query<IReadReleaseNote[], void>({
      query: () => '/api/v1.0/user_settings/release-note/',
      providesTags: ['ReadReleaseNotes'],
    }),
    markReleaseNoteRead: builder.mutation<unknown, { release_note_id: number }>({
      query: (body) => ({
        url: '/api/v1.0/user_settings/release-note/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['ReadReleaseNotes'],
    }),

    // ---- Project notifications (50/page) ----
    // The endpoint returns a plain array; pages are merged client-side (the
    // Backbone collection uses fetchPage with remove:false). We page-tag so a
    // PATCH (mark read) does not blow away the loaded pages.
    getProjectNotifications: builder.query<INotificationPage<IProjectNotification>, number>({
      query: (page = 1) => `/project/project_notifications/${MFG_ID}/?page=${page}`,
      transformResponse: (rows: IProjectNotification[], _meta, page): INotificationPage<IProjectNotification> => ({
        rows: rows ?? [],
        page: page ?? 1,
      }),
      providesTags: (_r, _e, page) => [{ type: 'Notifications' as const, id: page }],
    }),
    markNotificationRead: builder.mutation<unknown, { id: number }>({
      query: ({ id }) => ({
        url: `/project/project_notifications/${MFG_ID}/${id}/`,
        method: 'PATCH',
        body: { is_read: true },
      }),
      // Do NOT invalidate the pages — the UI marks the row read optimistically
      // (matches the Backbone in-place .read toggle without a re-fetch).
    }),

    // ---- Reports history (50/page) ----
    getReportsNotifications: builder.query<INotificationPage<IReportNotification>, number>({
      query: (page = 1) => `/reports/reports_notifications/${MFG_ID}/?page=${page}`,
      transformResponse: (rows: IReportNotification[], _meta, page): INotificationPage<IReportNotification> => ({
        rows: rows ?? [],
        page: page ?? 1,
      }),
      providesTags: (_r, _e, page) => [{ type: 'Reports' as const, id: page }],
    }),
    markReportRead: builder.mutation<unknown, { id: number }>({
      query: ({ id }) => ({
        url: `/reports/report_notification/${MFG_ID}/${id}/`,
        method: 'PATCH',
        body: { is_read: true },
      }),
      // In-place .read/.unread toggle in the UI (Backbone openReport/markAsRead).
    }),

    // ---- Reports unread count (X-Notification-Count header, 204) ----
    // The endpoint returns 204 with the count in the X-Notification-Count
    // header. We read it via the raw Response, exposed by `responseHandler`.
    getReportsCount: builder.query<number, void>({
      query: () => ({
        url: `/reports/count_reports_notification/${MFG_ID}/`,
        responseHandler: async (response: Response) => {
          const raw =
            response.headers.get('X-Notification-Count') ||
            response.headers.get('x-notification-count') ||
            '0';
          const n = parseInt(raw, 10);
          return Number.isNaN(n) ? 0 : n;
        },
      }),
      providesTags: ['ReportsCount'],
    }),
  }),
});

export const {
  useGetReleaseNotesAllQuery,
  useGetReleaseNotesQuery,
  useMarkReleaseNoteReadMutation,
  useGetProjectNotificationsQuery,
  useMarkNotificationReadMutation,
  useGetReportsNotificationsQuery,
  useMarkReportReadMutation,
  useGetReportsCountQuery,
} = headerApi;
